<template>
  <div
    class="w-92 p-4 bg-white shadow-xl space-y-4 border border-border-primary rounded-md"
    data-cy="stip-creator"
  >
    <loader :is-loading="createStipMutaiton.isPending.value" />
    <lf-input
      name="new-stip-name"
      class="min-w-32 w-full"
      :value="stip.name"
      :placeholder="$t('COMMON.NAME')"
      no-margin
      @key-released="stip.name = $event"
    />
    <lf-input
      name="new-stip-description"
      :value="stip.description"
      :placeholder="$t('COMMON.DESCRIPTION')"
      no-margin
      @key-released="stip.description = $event"
    />
    <stip-dropdown-categories v-model="stip.file_type" />
    <modal-buttons
      class="grow justify-end px-0"
      :on-save="createStipMutaiton.mutate"
      :on-cancel="() => emit('close')"
    />
  </div>
</template>

<script setup lang="ts">
import { reactive } from "vue";

import { useMutation, useQueryClient } from "@tanstack/vue-query";

import { useFilters } from "@/hooks/filters";
import { STIPS_QUERY_KEYS } from "@/hooks/queries/stips";
import stipsApiService from "@/services/modules/stips";

import StipDropdownCategories from "@/components/deals/StipDropdownCategories.vue";
import Loader from "@/components/ui/Loader.vue";
import ModalButtons from "@/components/ui/ModalButtons.vue";
import LfInput from "@/components/ui/inputs/LfInput.vue";

import type { IPaginatedResponse } from "@/models/common";
import type { StipDefinition } from "@/models/stips";
import type { InfiniteData } from "@tanstack/vue-query";

const emit = defineEmits<{
  close: [];
  "stip-created": [StipDefinition];
}>();

const queryClient = useQueryClient();

const { filterStateAsSearchParams } = useFilters();

const stip = reactive({
  name: "",
  description: "",
  file_type: undefined,
});

const createStipMutaiton = useMutation({
  mutationFn: () => stipsApiService.createStip(stip),
  onSuccess: (createdStip: StipDefinition) => {
    // For UX - When a new stip is created, push it to the top of the pagination
    queryClient.setQueryData(
      [
        STIPS_QUERY_KEYS.ROOT,
        STIPS_QUERY_KEYS.INFINITE,
        filterStateAsSearchParams,
      ],
      (
        oldData: InfiniteData<IPaginatedResponse<StipDefinition>> | undefined
      ) => {
        if (!oldData?.pages?.length) {
          return oldData;
        }

        const [firstPage, ...restPages] = oldData.pages;

        return {
          ...oldData,
          pages: [
            { ...firstPage, data: [createdStip, ...firstPage.data] },
            ...restPages,
          ],
        };
      }
    );

    emit("stip-created", createdStip);
    emit("close");
  },
});
</script>
